import Button from '@rio-cloud/rio-uikit/Button';
import { FormattedMessage, useIntl } from 'react-intl';

import { ServiceTypeName } from '#components/order/ServiceTypeName.tsx';
import type { OrderSummary } from '#domain/Order.ts';

import { StopInfo } from '../../shared/StopInfo.tsx';
import { LoadMetrics } from './LoadMetrics.tsx';
import type { StopLoadMetrics } from './loadMetrics.ts';
import { TourListCard } from './TourListCard.tsx';
import { canMoveStopInTour, type TourStop } from './TourStop.ts';
import { formatTourStopMarkerLabel, type TourStopSequence } from './TourStopSequenceMarker.tsx';

type StopRowProps = {
    item: TourStop;
    order?: OrderSummary;
    allStops: TourStop[];
    isMisplaced: boolean;
    loadMetrics?: StopLoadMetrics;
    canEdit?: boolean;
    onEditStop?: (stop: TourStop) => void;
    sequenceMarker?: TourStopSequence;
};

const stopIconClasses: Record<TourStop['type'], [string, string]> = {
    execute: ['rioglyph-wrench', 'display-none'],
    load: ['rioglyph-arrow-down margin-left--5', 'rioglyph-palette margin-left--3'],
    unload: ['rioglyph-palette margin-right--3', 'rioglyph-arrow-up margin-right--5'],
    unknown: ['rioglyph-palette margin-right--3', 'rioglyph-arrow-up margin-right--5'],
};

const StopIcon = ({ item }: { item: TourStop }) => {
    const [primaryIconClasses, secondaryIconClasses] = stopIconClasses[item.type];

    return (
        <div className='display-flex flex-column align-items-center gap-2'>
            <div className='display-flex justify-content-center'>
                <span className={`text-size-20 text-color-darker rioglyph ${primaryIconClasses}`} />
                <span className={`text-size-20 text-color-darker rioglyph ${secondaryIconClasses}`} />
            </div>
            <span className='text-size-12'>
                <FormattedMessage id={`intl-msg:ordercom-cloud-frontend.tours.stop.type.${item.type}`} />
            </span>
        </div>
    );
};

const DragHandler = () => (
    <div className='min-height-100pct display-flex align-items-center border border-left-only drag-handler cursor-grab flex-0'>
        <Button
            bsStyle='muted'
            iconOnly
            iconName='rioglyph-drag-n-drop'
            autoTrackingKey='ordercom.tours.create-dialog.stop-row.drag-handle'
            className='cursor-grab hover-bg-none'
        />
    </div>
);

const DragPlaceholder = () => (
    <div className='min-height-100pct display-flex align-items-center border border-left-only flex-0'>
        <Button
            bsStyle='muted'
            iconOnly
            iconName='rioglyph-drag-n-drop'
            className='opacity-30 hover-bg-none'
            disabled
        />
    </div>
);

const StopRowDetails = ({
    item,
    order,
    isMisplaced,
    loadMetrics,
    canEdit,
    onEditStop,
    sequenceMarker,
}: {
    item: TourStop;
    order?: OrderSummary;
    isMisplaced: boolean;
    loadMetrics?: StopLoadMetrics;
    canEdit?: boolean;
    onEditStop?: (stop: TourStop) => void;
    sequenceMarker?: TourStopSequence;
}) => {
    const intl = useIntl();

    return (
        <div className='display-flex flex-column justify-content-center min-width-0' data-testid='stop-row-details'>
            <div className='display-flex align-items-start gap-10' data-testid='stop-row-main-content'>
                <div className='flex-1-1-0 min-width-0'>
                    <StopInfo
                        addressFirst={Boolean(sequenceMarker)}
                        name={item.type === 'execute' ? <ServiceTypeName value={item.name} /> : item.name}
                        streetAddress={item.location.street}
                        city={item.location.city}
                        country={item.location.country}
                        zipCode={item.location.zipCode}
                        from={item.start}
                        to={item.end}
                        canEdit={canEdit && !sequenceMarker}
                        metadata={
                            sequenceMarker && (
                                <>
                                    <span className='display-flex align-items-center gap-5'>
                                        <span className='rioglyph rioglyph-order' aria-hidden='true' />
                                        {item.orderNumber ?? item.customer}
                                    </span>
                                    {canEdit && (
                                        <Button
                                            bsStyle={Button.MUTED}
                                            bsSize={Button.XS}
                                            iconName='rioglyph-pencil'
                                            onClick={() => onEditStop?.(item)}
                                        >
                                            <FormattedMessage id='intl-msg:ordercom-cloud-frontend.tours.stop.edit.title' />
                                        </Button>
                                    )}
                                </>
                            )
                        }
                        onEdit={() => onEditStop?.(item)}
                        stopStatus={item.status}
                        stopType={item.type}
                        orderStatus={order?.status}
                        showStatusInfo
                    />
                </div>
                <div
                    className='flex-0 display-flex flex-column align-items-center gap-5'
                    data-testid='stop-row-order-badge'
                >
                    {!sequenceMarker && (
                        <span className={`label label-${getLabel(item.group)}`}>
                            {item.type === 'execute' ? (
                                <ServiceTypeName value={item.orderNumber ?? item.name} />
                            ) : (
                                (item.orderNumber ?? item.customer)
                            )}
                        </span>
                    )}
                    {sequenceMarker && (
                        <span
                            className='label label-info text-nowrap'
                            role='img'
                            aria-label={formatTourStopMarkerLabel(intl, sequenceMarker)}
                            title={item.orderNumber ?? item.customer}
                        >
                            #{sequenceMarker.sequence}
                        </span>
                    )}
                </div>
            </div>
            {loadMetrics && (
                <LoadMetrics metrics={loadMetrics} stopType={item.type} summary={Boolean(sequenceMarker)} />
            )}
            {isMisplaced && (
                <span className='position-absolute bottom--1 right-15 bg-danger padding-x-10 padding-y-3 rounded-top text-size-12 pointer-events-none'>
                    <FormattedMessage id='intl-msg:ordercom-cloud-frontend.tours.create-dialog.validation.stop-order' />
                </span>
            )}
        </div>
    );
};

export const StopRow = ({
    item,
    order,
    allStops,
    isMisplaced,
    loadMetrics,
    canEdit,
    onEditStop,
    sequenceMarker,
}: StopRowProps) => (
    <div className='display-flex justify-content-between align-items-center gap-5 min-width-0'>
        <TourListCard
            leadingWidth={sequenceMarker ? 60 : 70}
            leading={<StopIcon item={item} />}
            trailing={canMoveStopInTour(allStops, item, order) ? <DragHandler /> : <DragPlaceholder />}
            panelStyle={
                isMisplaced || (!sequenceMarker && Boolean(loadMetrics?.exceededCapacity?.length))
                    ? 'danger'
                    : 'default'
            }
            shadow
            testId='tour-stop-card'
        >
            <StopRowDetails
                item={item}
                order={order}
                isMisplaced={isMisplaced}
                loadMetrics={loadMetrics}
                canEdit={canEdit}
                onEditStop={onEditStop}
                sequenceMarker={sequenceMarker}
            />
        </TourListCard>
    </div>
);

const getLabel = (group: number): string => {
    const labels = ['primary', 'secondary', 'info', 'success', 'warning'];
    return labels[group % labels.length];
};
